/**
 * A9 B1 selective signing read-model.
 * Maps A4 verification/reconcile fields + RBAC. Does not reimplement A4 gates.
 */

import type { CaseTimelineEvent } from '../../services/reportsApi';
import { presentScanControlRelation } from './scanControlTemporalRelation';

export const SIGN_THIS_SAFETY_SCAN = 'Sign this Safety Scan';
export const SIGN_THIS_SCAN_EXPLANATION = (
  'Signing creates an immutable Signed Record for this Safety Scan. Other Safety Scans remain unsigned.'
);

export type ScanSigningState =
  | 'ready_to_sign'
  | 'verification_required'
  | 'already_signed'
  | 'not_signable';

export type ScanSigningView = {
  reportId: string;
  eligible: boolean;
  showSignCta: boolean;
  state: ScanSigningState;
  reasonCode: string | null;
  reason: string;
  unresolvedCount: number;
  unresolvedDisclosed: boolean;
  isCurrentScan: boolean;
  isLatestSigned: boolean;
};

export type CaseScanRegisterRow = {
  reportId: string;
  status: string;
  unsigned: boolean;
  windowStart: string | null;
  windowEnd: string | null;
  isCurrentScan: boolean;
  isLatestSigned: boolean;
  revision: string | number | null;
  relation: 'PRE' | 'CROSSING' | 'POST' | null;
};

function asText(value: unknown): string {
  return String(value || '').trim();
}

export function presentScanSigning(args: {
  reportId: string;
  reportStatus?: string | null;
  verificationState?: string | null;
  progressComplete?: boolean;
  unreviewed?: number | null;
  unresolved?: number | null;
  unresolvedDisclosed?: boolean;
  reconciliationReady?: boolean;
  canSign?: boolean;
  currentScanReportId?: string | null;
  latestSignedReportId?: string | null;
}): ScanSigningView {
  const reportId = asText(args.reportId);
  const status = asText(args.reportStatus).toLowerCase();
  const verificationState = asText(args.verificationState).toUpperCase();
  const unreviewed = Number(args.unreviewed || 0);
  const unresolved = Number(args.unresolved || 0);
  const complete = Boolean(args.progressComplete) && unreviewed <= 0;
  const isCurrentScan = Boolean(reportId) && reportId === asText(args.currentScanReportId);
  const isLatestSigned = Boolean(reportId) && reportId === asText(args.latestSignedReportId);
  const alreadySigned = (
    verificationState === 'FINALIZED_IMMUTABLE'
    || status === 'finalized'
  );
  if (!reportId) {
    return {
      reportId: '',
      eligible: false,
      showSignCta: false,
      state: 'not_signable',
      reasonCode: 'missing_report_id',
      reason: 'This Safety Scan was not found.',
      unresolvedCount: unresolved,
      unresolvedDisclosed: Boolean(args.unresolvedDisclosed) || unresolved > 0,
      isCurrentScan,
      isLatestSigned,
    };
  }
  if (alreadySigned) {
    return {
      reportId,
      eligible: false,
      showSignCta: false,
      state: 'already_signed',
      reasonCode: 'already_signed',
      reason: 'This Safety Scan is already an immutable Signed Record.',
      unresolvedCount: unresolved,
      unresolvedDisclosed: Boolean(args.unresolvedDisclosed) || unresolved > 0,
      isCurrentScan,
      isLatestSigned,
    };
  }
  if (!complete || verificationState === 'NOT_STARTED' || verificationState === 'FREEZE_NOT_READY') {
    return {
      reportId,
      eligible: false,
      showSignCta: false,
      state: 'verification_required',
      reasonCode: 'verification_required',
      reason: 'Complete formal verification for this Safety Scan before signing.',
      unresolvedCount: unresolved,
      unresolvedDisclosed: Boolean(args.unresolvedDisclosed) || unresolved > 0,
      isCurrentScan,
      isLatestSigned,
    };
  }
  if (!args.canSign) {
    return {
      reportId,
      eligible: false,
      showSignCta: false,
      state: 'not_signable',
      reasonCode: 'sign_capability_required',
      reason: 'Signing this Safety Scan requires the signed_scan:sign capability.',
      unresolvedCount: unresolved,
      unresolvedDisclosed: Boolean(args.unresolvedDisclosed) || unresolved > 0,
      isCurrentScan,
      isLatestSigned,
    };
  }
  if (!args.reconciliationReady) {
    return {
      reportId,
      eligible: false,
      showSignCta: false,
      state: 'verification_required',
      reasonCode: 'reconciliation_pending',
      reason: 'Verification complete. Measurement reconciliation pending.',
      unresolvedCount: unresolved,
      unresolvedDisclosed: Boolean(args.unresolvedDisclosed) || unresolved > 0,
      isCurrentScan,
      isLatestSigned,
    };
  }
  return {
    reportId,
    eligible: true,
    showSignCta: true,
    state: 'ready_to_sign',
    reasonCode: null,
    reason: SIGN_THIS_SCAN_EXPLANATION,
    unresolvedCount: unresolved,
    unresolvedDisclosed: Boolean(args.unresolvedDisclosed) || unresolved > 0,
    isCurrentScan,
    isLatestSigned,
  };
}

function related(event: CaseTimelineEvent): Record<string, unknown> {
  const blob = event.related;
  return blob && typeof blob === 'object' && !Array.isArray(blob)
    ? blob as Record<string, unknown>
    : {};
}

export function presentCaseScanRegister(args: {
  events?: CaseTimelineEvent[] | null;
  currentScanReportId?: string | null;
  latestSignedReportId?: string | null;
  controlRevisionId?: string | null;
  implementedAt?: string | null;
}): CaseScanRegisterRow[] {
  const current = asText(args.currentScanReportId);
  const latestSigned = asText(args.latestSignedReportId);
  const byId = new Map<string, CaseScanRegisterRow>();
  for (const event of args.events || []) {
    if (asText(event.event_type) !== 'safety_scan') continue;
    const extra = related(event);
    const reportId = asText(extra.report_id) || asText(event.source?.id);
    if (!reportId) continue;
    const status = asText(extra.status).toLowerCase() || 'draft';
    const windowStart = asText(extra.window_start_utc) || null;
    const windowEnd = asText(extra.window_end_utc) || null;
    const relation = presentScanControlRelation({
      windowStart,
      windowEnd,
      controlRevisionId: args.controlRevisionId,
      implementedAt: args.implementedAt,
    });
    byId.set(reportId, {
      reportId,
      status,
      unsigned: extra.unsigned !== false && status !== 'finalized',
      windowStart,
      windowEnd,
      isCurrentScan: reportId === current,
      isLatestSigned: reportId === latestSigned,
      revision: event.source?.revision ?? null,
      relation: relation.relation === 'NOT_APPLICABLE' ? null : relation.relation,
    });
  }
  return Array.from(byId.values()).sort((left, right) => {
    const end = asText(left.windowEnd).localeCompare(asText(right.windowEnd));
    if (end) return end;
    return left.reportId.localeCompare(right.reportId);
  });
}

export function customerSafeFinalizeError(payload: {
  error?: string | null;
  blockers?: Array<{ code?: string | null; message?: string | null }> | null;
  detail?: string | null;
} | null | undefined): { code: string; message: string } {
  const error = asText(payload?.error);
  const blockers = Array.isArray(payload?.blockers) ? payload!.blockers! : [];
  const separation = blockers.find(
    (item) => asText(item.code).toUpperCase() === 'REVIEWER_SIGNER_SEPARATION_REQUIRED',
  );
  if (separation || error.toUpperCase() === 'REVIEWER_SIGNER_SEPARATION_REQUIRED') {
    return {
      code: 'REVIEWER_SIGNER_SEPARATION_REQUIRED',
      message: asText(separation?.message) || (
        'The signer must be distinct from every formal Scan reviewer whose dispositions constitute this verification population.'
      ),
    };
  }
  if (error === 'forbidden' || error === 'unauthorized') {
    return {
      code: 'sign_capability_required',
      message: 'You can inspect this Safety Scan, but you do not have permission to sign it.',
    };
  }
  if (error === 'report_not_draft') {
    return {
      code: 'already_signed',
      message: 'This Safety Scan is already an immutable Signed Record.',
    };
  }
  if (error === 'report_revision_conflict') {
    return {
      code: 'report_revision_conflict',
      message: 'This Safety Scan changed before signing. Reload this Scan and try again. The request was not switched to another Scan.',
    };
  }
  const first = blockers[0];
  if (first) {
    return {
      code: asText(first.code) || 'report_finalize_blocked',
      message: asText(first.message) || 'This Safety Scan cannot be signed yet.',
    };
  }
  return {
    code: error || 'report_finalize_failed',
    message: asText(payload?.detail) || 'This Safety Scan could not be signed.',
  };
}
